import { useMemo } from 'react'

import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { parseOptionalISODate, useIsPast } from '@b-stock/fe-core/bstock-react'

import { BidFormProvider } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { useAuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import useAuctionDetailsUpdates from '@/buyer-portal/components/features/listing/hooks/useAuctionDetailsUpdates'
import useOrderForWon from '@/buyer-portal/components/features/listing/hooks/useOrderForWon'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'

import { MissingDataBanner } from '../../MissingDataBanner'
import { AuctionActionColumnContent } from './AuctionActionColumnContent'

export const AuctionActionColumn = ({
  listing,
  initialBidAmount,
}: {
  listing: OrchestratedAuctionListingDetails
  initialBidAmount?: string
}) => {
  if (!listing.lot._id) {
    return (
      <MissingDataBanner
        titleId="Auction.details.actionColumn.missingData.title"
        messageId="Auction.details.actionColumn.missingData.lot"
        log={{
          level: 'warn',
          message: 'AuctionActionColumn: listing missing lot id',
          metadata: {
            listingId: listing._id,
            missingField: 'lot._id',
          },
        }}
      />
    )
  }

  return (
    <AuctionActionColumnWithLot
      listing={listing}
      initialBidAmount={initialBidAmount}
    />
  )
}

const AuctionActionColumnWithLot = ({
  listing,
  initialBidAmount,
}: {
  listing: OrchestratedAuctionListingDetails
  initialBidAmount?: string
}) => {
  const order = useOrderForWon(
    listing._id,
    listing.auction.bidStatus,
    listing.listingType === 'CONTRACT'
  )

  const endDate = useMemo(
    () => parseOptionalISODate(listing.dateVisibilityEnds),
    [listing.dateVisibilityEnds]
  )

  const isVisibilityEnded = useIsPast(endDate)
  const auctionDetailState = useAuctionDetailState(listing)

  useAuctionDetailsUpdates(listing)

  const { data: sellerSubscription } = activeSubscriptionsQuery.useQuery(
    listing.accountId
  )

  const parsedInitialBidAmount = isNaN(Number(initialBidAmount))
    ? undefined
    : Number(initialBidAmount)

  return (
    <BidFormProvider
      auctionDetailState={auctionDetailState}
      bidLocation="pdp"
      // 002-multi-currency / FR-032 — thread the listing's currency
      // through to the bid form so CAD/GBP/EUR listings render their
      // originating symbol (CA$, £, €) instead of a bare `$`. Mirrors
      // the ListView path (AuctionActionCellPostload, T263/T264). The
      // installed @b-stock/listing-api-client predates the multi-currency
      // rollout so `currency` isn't typed on ListingDto yet; cast through
      // to read it. USD fallback preserves SC-007 for legacy listings.
      currency={(listing as { currency?: string }).currency ?? 'USD'}
      initialBidAmount={parsedInitialBidAmount}
      listing={listing}
      sellerSubscription={sellerSubscription}
    >
      <AuctionActionColumnContent
        auctionDetailState={auctionDetailState}
        isVisibilityEnded={isVisibilityEnded}
        listing={listing}
        order={order}
      />
    </BidFormProvider>
  )
}
